/**
 * Pure work-email check (specs/email-capture.md section 3). No imports: the
 * domain lists are passed in, so the tests and the form share one function.
 */

export const MAX_LENGTH = 254;
const INVALID_MESSAGE = 'Enter your work email';

// The whitespace stripped from the ends and refused inside an address: what
// trim() and \s cover, spelled out because start_run uses the same list
// (private.normalise_email); test/sql/email-cases.json runs on both sides.
const SPACE = '\\t\\n\\v\\f\\r \\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000\\ufeff';
const EDGE_SPACE = new RegExp(`^[${SPACE}]+|[${SPACE}]+$`, 'g');
const ANY_SPACE = new RegExp(`[${SPACE}]`);
// What an address may hold, the same as private.email_well_formed: a local
// part of letters, digits and _ ' + - (dots only between them, starting with
// a letter, digit or _), then dotted labels of letters, digits and inner
// hyphens, ending in two or more letters. Keeps markup and spreadsheet
// formulas (=, |, <, ") out of the database and HubSpot.
const WELL_FORMED = /^[a-z0-9_][a-z0-9_'+-]*(?:\.[a-z0-9_'+-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

/**
 * Trim and lower-case an address.
 * @param {string} text
 * @returns {string}
 */
export function normaliseEmail(text) {
  return String(text == null ? '' : text).replace(EDGE_SPACE, '').toLowerCase();
}

/**
 * Build the lookup sets from the dist/email-domains.json shape.
 * @param {{blocked?: string[], allowed?: string[]}} json
 * @returns {{blocked: Set<string>, allowed: Set<string>}}
 */
export function listsFromJson(json) {
  const clean = (list) => new Set((Array.isArray(list) ? list : [])
    .map((d) => String(d).trim().toLowerCase()).filter(Boolean));
  return { blocked: clean(json && json.blocked), allowed: clean(json && json.allowed) };
}

/**
 * True when the domain, or any parent domain of it, is in the set.
 * @param {string} domain
 * @param {Set<string>} set
 * @returns {boolean}
 */
function matchesDomainOrParent(domain, set) {
  const labels = domain.split('.');
  // every suffix down to the bare top-level label, as the server does
  for (let i = 0; i < labels.length; i++) {
    if (set.has(labels.slice(i).join('.'))) return true;
  }
  return false;
}

/**
 * Check a typed address. A domain is blocked if it or a parent is blocked,
 * unless it or a parent is allowed.
 * @param {string} text
 * @param {{blocked: Set<string>, allowed: Set<string>}} lists
 * @returns {{ok: true, email: string} | {ok: false, reason: 'invalid'|'blocked', domain: string, message: string}}
 */
export function checkEmail(text, lists) {
  const email = normaliseEmail(text);
  const invalid = (domain = '') => ({ ok: false, reason: 'invalid', domain, message: INVALID_MESSAGE });

  // counted in code points, as the server's char_length does
  if (!email || [...email].length > MAX_LENGTH || ANY_SPACE.test(email)) return invalid();
  const domain = email.split('@').pop();
  if (!WELL_FORMED.test(email)) return invalid(domain);

  const blocked = lists && lists.blocked ? lists.blocked : new Set();
  const allowed = lists && lists.allowed ? lists.allowed : new Set();
  if (matchesDomainOrParent(domain, blocked) && !matchesDomainOrParent(domain, allowed)) {
    return { ok: false, reason: 'blocked', domain, message: `Please use your work email - we can't accept ${domain}` };
  }
  return { ok: true, email };
}
